import React from "react";
import { apiUrl } from "../../config/apiBase";
import InfoIkona from "./AdminInfoIkona";
import { adminHypotekyInfoTexty } from "./adminHypotekyInfoTexty";

const dnes = new Date().toISOString().slice(0, 10);
const MAX_DLZKA_NAZVU_KAMPANE = 80;
const MAX_POCET_VEKOVYCH_LIMITOV = 4;
const MAX_DLZKA_POZNAMKY_KAMPANE = 600;

const prazdnyFormular = {
  bankaKod: "",
  nazov: "",
  aktivna: true,
  datumOd: dnes,
  datumDo: "",
  priorita: "5",
  hintText: "",
  typyHypoteky: [],
  fixacieRoky: [],
  fixacieMesiace: [],
  vlastnaFixaciaTyp: "rocna",
  vlastnaFixaciaHodnota: "",
  ltvMax: "",
  datumDoNeurcito: true,
  vekRezim: "ziadny",
  vekMin: "",
  vekMax: "",
  vekAplikacia: "ZIADNA",
  vekPravidla: [],
  musiaBytZapnuteAktivne: false,
  musiaBytZapnute: [],
  spravanieRezim: "STANDARD",
  vypnuteCheckboxy: [],
  sadzbaTyp: "ZIADNY",
  sadzbaHodnota: "",
  poplatokTyp: "ZIADNY",
  poplatokHodnota: "",
  prilohaPdf: {
    fileId: null,
    nazov: null,
    mime: null,
    velkost: null
  }
};

const stavOptions = [
  { kod: "", nazov: "Všetky" },
  { kod: "aktivne_datumovo", nazov: "Aktívne dátumovo" },
  { kod: "neaktivne_datumovo", nazov: "Neaktívne dátumovo" },
  { kod: "manualne_vypnute", nazov: "Manuálne vypnuté" }
];

const vekRezimOptions = [
  { kod: "ziadny", nazov: "Bez podmienky" },
  { kod: "jednoduchy", nazov: "Jednoduchý limit" },
  { kod: "pravidla", nazov: "Rozšírené pravidlá" }
];

const vekPravidloSablony = [
  {
    kod: "jeden",
    nazov: "1 žiadateľ",
    preKoho: "JEDEN",
    aplikacia: "JEDINY",
    limity: [{ faza: "PODANIE", koho: "JEDINY", min: "", max: "" }]
  },
  {
    kod: "vsetci",
    nazov: "2 až 4 žiadatelia - každý",
    preKoho: "VIACERI",
    aplikacia: "VSETCI",
    limity: [{ faza: "PODANIE", koho: "VSETCI", min: "", max: "" }]
  },
  {
    kod: "najmladsi_najstarsi",
    nazov: "2 až 4 žiadatelia - najmladší/najstarší",
    preKoho: "VIACERI",
    aplikacia: "NAJMLADSI_NAJSTARSI",
    limity: [
      { faza: "PODANIE", koho: "NAJMLADSI", min: "", max: "" },
      { faza: "DOSPLATENIE", koho: "NAJSTARSI", min: "", max: "" }
    ]
  }
];

function cisloNaInput(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(Math.abs(n)).replace(".", ",");
}

function normalizujInputCislo(v) {
  return String(v || "")
    .replace(/\s/g, "")
    .replace(",", ".");
}

function normalizujEnum(v) {
  return String(v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function hasCislo(hodnota) {
  return normalizujInputCislo(hodnota) !== "";
}

function normalizujCheckboxKod(kod) {
  return kod === "ekoCertifikat" ? "maEkoCertifikat" : kod;
}

function normalizujCheckboxPole(hodnoty) {
  return Array.from(
    new Set(
      (Array.isArray(hodnoty) ? hodnoty : [])
        .map(normalizujCheckboxKod)
        .filter(Boolean)
    )
  );
}

function cislaPole(hodnoty) {
  return (Array.isArray(hodnoty) ? hodnoty : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
}

function formatDatum(hodnota) {
  if (!hodnota) return "Neurčito";
  const casti = String(hodnota).slice(0, 10).split("-");
  if (casti.length !== 3) return hodnota;
  const [rok, mesiac, den] = casti;
  if (!rok || !mesiac || !den) return hodnota;
  return `${den}.${mesiac}.${rok.slice(-2)}`;
}

function datumNaCas(hodnota) {
  if (!hodnota) return Number.MAX_SAFE_INTEGER;
  const cas = new Date(`${String(hodnota).slice(0, 10)}T00:00:00`).getTime();
  return Number.isFinite(cas) ? cas : Number.MAX_SAFE_INTEGER;
}

function formatPercento(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${Math.abs(n).toFixed(2).replace(".", ",")} %`;
}

function popisFixacii(formular) {
  const roky = (formular.fixacieRoky || []).map((v) => `${v}R`);
  const mesiace = (formular.fixacieMesiace || []).map((v) => `${v}M`);
  const vsetky = [...roky, ...mesiace];
  return vsetky.length ? vsetky.join(", ") : "Bez obmedzenia fixácie";
}

function labelJednoduchaAplikacia(kod) {
  const mapa = {
    ZIADNA: "Bez podmienky",
    VSETCI: "Všetci žiadatelia",
    ASPON_JEDEN: "Aspoň jeden žiadateľ",
    NAJMLADSI: "Najmladší žiadateľ",
    NAJSTARSI: "Najstarší žiadateľ"
  };
  return mapa[kod] || kod;
}

function labelPreKoho(kod) {
  const mapa = {
    VSETCI: "Všetci žiadatelia",
    JEDEN: "1 žiadateľ",
    VIACERI: "2 až 4 žiadatelia"
  };
  return mapa[kod] || kod;
}

function labelAplikaciaPravidla(kod) {
  const mapa = {
    VSETCI: "Každý musí spĺňať limit",
    JEDINY: "Jediný žiadateľ",
    NAJMLADSI_NAJSTARSI: "Kombinácia pravidiel pre najmladšieho/najstaršieho",
    ASPON_JEDEN: "Stačí aspoň jeden"
  };
  return mapa[kod] || kod;
}

function labelFaza(kod) {
  const mapa = {
    PODANIE: "Pri podaní",
    DOSPLATENIE: "Pri dosplatení"
  };
  return mapa[kod] || kod;
}

function labelKoho(kod) {
  const mapa = {
    VSETCI: "Všetci",
    JEDINY: "Jediný",
    NAJMLADSI: "Najmladší",
    NAJSTARSI: "Najstarší"
  };
  return mapa[kod] || kod;
}

function nazovStavu(kod) {
  const mapa = {
    aktivna_datumovo: "Aktívna dátumovo",
    neaktivna_datumovo: "Neaktívna dátumovo",
    manualne_vypnute: "Manuálne vypnutá"
  };
  return mapa[kod] || kod || "";
}

function stavClass(kod) {
  if (kod === "aktivna_datumovo") return "admin-status admin-status--active";
  if (kod === "manualne_vypnute") return "admin-status admin-status--disabled";
  return "admin-status admin-status--onboarding";
}

function popisEfektu(kampan) {
  const sadzba = kampan?.efekt?.sadzba || {};
  const poplatok = kampan?.efekt?.poplatok || {};
  const riadky = [];

  if (sadzba.typ === "PEVNA") riadky.push(`Sadzba pevná ${formatPercento(sadzba.hodnota)}`);
  if (sadzba.typ === "ZLAVA") riadky.push(`Zľava zo sadzby ${formatPercento(sadzba.hodnota)}`);
  if (poplatok.typ === "PERCENTO") riadky.push(`Zľava z poplatku ${formatPercento(poplatok.hodnota)}`);
  if (poplatok.typ === "FIX") riadky.push(`Nový poplatok ${Number(poplatok.hodnota || 0).toFixed(0)} €`);

  return riadky.length ? riadky.join(" · ") : "Bez efektu";
}

function noveVekPravidlo(meta) {
  return {
    preKoho: meta?.kampane?.vek?.preKoho?.[0] || "VSETCI",
    aplikacia: meta?.kampane?.vek?.aplikaciePravidla?.[0] || "VSETCI",
    limity: [
      {
        faza: "PODANIE",
        koho: "VSETCI",
        min: "",
        max: ""
      }
    ]
  };
}

function vytvorPravidloZoSablony(kod) {
  const sablona = vekPravidloSablony.find((p) => p.kod === kod) || vekPravidloSablony[0];

  return {
    preKoho: sablona.preKoho,
    aplikacia: sablona.aplikacia,
    limity: sablona.limity.map((l) => ({ ...l }))
  };
}

function formularZKampane(kampan, meta) {
  const vek = kampan?.podmienky?.vek || {};
  const maPravidla = Array.isArray(vek.pravidla) && vek.pravidla.length > 0;
  const maJednoduchy =
    !maPravidla &&
    ((vek.min !== null && vek.min !== undefined) ||
      (vek.max !== null && vek.max !== undefined) ||
      (vek.aplikacia && vek.aplikacia !== "ZIADNA"));

  return {
    ...prazdnyFormular,
    bankaKod: kampan?.bankaKod || "",
    nazov: kampan?.nazov || "",
    aktivna: kampan?.aktivna !== false,
    datumOd: kampan?.datumOd || dnes,
    datumDo: kampan?.datumDo || "",
    datumDoNeurcito: !kampan?.datumDo,
    priorita: cisloNaInput(kampan?.meta?.priorita),
    hintText: kampan?.hintText || "",
    typyHypoteky: Array.isArray(kampan?.podmienky?.typyHypoteky) ? kampan.podmienky.typyHypoteky : [],
    fixacieRoky: cislaPole(kampan?.podmienky?.fixacieRoky),
    fixacieMesiace: cislaPole(kampan?.podmienky?.fixacieMesiace),
    vlastnaFixaciaTyp: "rocna",
    vlastnaFixaciaHodnota: "",
    ltvMax: cisloNaInput(kampan?.podmienky?.ltv?.max),
    vekRezim: maPravidla ? "pravidla" : maJednoduchy ? "jednoduchy" : "ziadny",
    vekMin: cisloNaInput(vek.min),
    vekMax: cisloNaInput(vek.max),
    vekAplikacia: normalizujEnum(vek.aplikacia || "ZIADNA"),
    vekPravidla: maPravidla
      ? vek.pravidla.map((p) => ({
          preKoho: normalizujEnum(p.preKoho || "VSETCI"),
          aplikacia: normalizujEnum(p.aplikacia || "VSETCI"),
          limity: Array.isArray(p.limity) && p.limity.length
            ? p.limity.map((l) => ({
                faza: normalizujEnum(l.faza || "PODANIE"),
                koho: normalizujEnum(l.koho || "VSETCI"),
                min: cisloNaInput(l.min),
                max: cisloNaInput(l.max)
              }))
            : noveVekPravidlo(meta).limity
        }))
      : [],
    musiaBytZapnuteAktivne: normalizujCheckboxPole(kampan?.podmienky?.checkboxy?.podmienka?.musiaBytZapnute).length > 0,
    musiaBytZapnute: normalizujCheckboxPole(kampan?.podmienky?.checkboxy?.podmienka?.musiaBytZapnute),
    spravanieRezim: kampan?.spravanieCheckboxov?.rezim || "STANDARD",
    vypnuteCheckboxy: normalizujCheckboxPole(kampan?.spravanieCheckboxov?.vypnuteCheckboxy),
    sadzbaTyp: kampan?.efekt?.sadzba?.typ || "ZIADNY",
    sadzbaHodnota: cisloNaInput(kampan?.efekt?.sadzba?.hodnota),
    poplatokTyp: kampan?.efekt?.poplatok?.typ || "ZIADNY",
    poplatokHodnota: cisloNaInput(kampan?.efekt?.poplatok?.hodnota),
    prilohaPdf: kampan?.prilohaPdf || prazdnyFormular.prilohaPdf
  };
}

function formularNaBody(formular) {
  const vlastnaFixacia = Number(normalizujInputCislo(formular.vlastnaFixaciaHodnota));
  const pouzitVlastnuFixaciu = Number.isFinite(vlastnaFixacia) && vlastnaFixacia > 0;
  const fixacieRoky = pouzitVlastnuFixaciu ? [] : [...formular.fixacieRoky];
  const fixacieMesiace = pouzitVlastnuFixaciu ? [] : [...formular.fixacieMesiace];

  if (pouzitVlastnuFixaciu) {
    const ciel = formular.vlastnaFixaciaTyp === "mesacna" ? fixacieMesiace : fixacieRoky;
    ciel.push(vlastnaFixacia);
  }

  const vek =
    formular.vekRezim === "pravidla"
      ? {
          pravidla: formular.vekPravidla.map((p) => ({
            preKoho: p.preKoho,
            aplikacia: p.aplikacia,
            limity: p.limity.map((l) => ({
              faza: l.faza,
              koho: l.koho,
              min: normalizujInputCislo(l.min),
              max: normalizujInputCislo(l.max)
            }))
          }))
        }
      : formular.vekRezim === "jednoduchy"
        ? {
            min: normalizujInputCislo(formular.vekMin),
            max: normalizujInputCislo(formular.vekMax),
            aplikacia: formular.vekAplikacia
          }
        : {
            min: null,
            max: null,
            aplikacia: "ZIADNA"
          };

  return {
    bankaKod: formular.bankaKod,
    nazov: formular.nazov,
    aktivna: formular.aktivna,
    datumOd: formular.datumOd,
    datumDo: formular.datumDoNeurcito ? null : formular.datumDo || null,
    hintText: formular.hintText,
    meta: {
      priorita: normalizujInputCislo(formular.priorita)
    },
    podmienky: {
      typyHypoteky: formular.typyHypoteky,
      fixacieRoky: fixacieRoky.sort((a, b) => a - b),
      fixacieMesiace: fixacieMesiace.sort((a, b) => a - b),
      ltv: {
        max: normalizujInputCislo(formular.ltvMax)
      },
      vek,
      checkboxy: {
        podmienka: {
          musiaBytZapnute: formular.musiaBytZapnute
        }
      }
    },
    spravanieCheckboxov: {
      rezim: formular.spravanieRezim,
      vypnuteCheckboxy: formular.vypnuteCheckboxy
    },
    efekt: {
      sadzba: {
        typ: formular.sadzbaTyp,
        hodnota: normalizujInputCislo(formular.sadzbaHodnota)
      },
      poplatok: {
        typ: formular.poplatokTyp,
        hodnota: normalizujInputCislo(formular.poplatokHodnota)
      }
    },
    prilohaPdf: formular.prilohaPdf
  };
}

function AdminHypotekyKampane() {
  const [meta, setMeta] = React.useState({ banky: [], typyHypoteky: [], kampane: {} });
  const [kampane, setKampane] = React.useState([]);
  const [filterBankaKod, setFilterBankaKod] = React.useState("");
  const [filterStav, setFilterStav] = React.useState("");
  const [zoradenie, setZoradenie] = React.useState({ stlpec: "", smer: "asc" });
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editKampan, setEditKampan] = React.useState(null);
  const [formular, setFormular] = React.useState(prazdnyFormular);
  const [modalError, setModalError] = React.useState("");
  const [modalSaving, setModalSaving] = React.useState(false);
  const [deleteKampan, setDeleteKampan] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const infoTexty = adminHypotekyInfoTexty[editKampan ? "kampaneUpravit" : "kampanePridat"];

  const banky = React.useMemo(() => Array.isArray(meta?.banky) ? meta.banky : [], [meta?.banky]);
  const typyHypoteky = React.useMemo(() => Array.isArray(meta?.typyHypoteky) ? meta.typyHypoteky : [], [meta?.typyHypoteky]);
  const checkboxy = React.useMemo(() => Array.isArray(meta?.kampane?.checkboxy) ? meta.kampane.checkboxy : [], [meta?.kampane?.checkboxy]);
  const fixaciePreFormular = React.useMemo(() => {
    const vsetkyFixacie = Array.isArray(meta?.fixacie) ? meta.fixacie : [];
    if (!formular.bankaKod) return vsetkyFixacie;

    return vsetkyFixacie.filter((f) => {
      const bankyFixacie = Array.isArray(f.banky) ? f.banky : [];
      return !bankyFixacie.length || bankyFixacie.includes(formular.bankaKod);
    });
  }, [meta?.fixacie, formular.bankaKod]);

  const fixacieRokyOptions = React.useMemo(
    () => Array.from(
      new Set([
        ...fixaciePreFormular
          .map((f) => Number(f.fixaciaRoky))
          .filter((v) => Number.isFinite(v) && v > 0),
        ...(formular.fixacieRoky || [])
      ])
    ).sort((a, b) => a - b),
    [fixaciePreFormular, formular.fixacieRoky]
  );

  const fixacieMesiaceOptions = React.useMemo(
    () => Array.from(
      new Set([
        ...fixaciePreFormular
          .map((f) => Number(f.fixaciaMesiace))
          .filter((v) => Number.isFinite(v) && v > 0),
        ...(formular.fixacieMesiace || [])
      ])
    ).sort((a, b) => a - b),
    [fixaciePreFormular, formular.fixacieMesiace]
  );

  const zobrazeneKampane = React.useMemo(() => {
    const smer = zoradenie.smer === "desc" ? -1 : 1;
    const porovnajText = (a, b) => String(a || "").localeCompare(String(b || ""), "sk", { sensitivity: "base" });
    const poradieStavu = (kampan) => kampan.stav === "aktivna_datumovo" ? 0 : 1;

    return kampane.map((kampan, index) => ({ kampan, index })).sort((aZaznam, bZaznam) => {
      const a = aZaznam.kampan;
      const b = bZaznam.kampan;
      const stavovyRozdiel = poradieStavu(a) - poradieStavu(b);
      if (stavovyRozdiel !== 0) return stavovyRozdiel;

      if (!zoradenie.stlpec) return aZaznam.index - bZaznam.index;

      let vysledok = 0;

      if (zoradenie.stlpec === "banka") {
        vysledok = porovnajText(a.bankaKod, b.bankaKod);
      } else if (zoradenie.stlpec === "platnost") {
        vysledok = datumNaCas(a.datumOd) - datumNaCas(b.datumOd);
        if (vysledok === 0) vysledok = datumNaCas(a.datumDo) - datumNaCas(b.datumDo);
      } else if (zoradenie.stlpec === "stav") {
        vysledok = porovnajText(nazovStavu(a.stav), nazovStavu(b.stav));
      } else if (zoradenie.stlpec === "priorita") {
        vysledok = (Number(a.meta?.priorita) || 0) - (Number(b.meta?.priorita) || 0);
      }

      return vysledok * smer || aZaznam.index - bZaznam.index;
    }).map(({ kampan }) => kampan);
  }, [kampane, zoradenie]);

  const prepniZoradenie = (stlpec) => {
    setZoradenie((prev) => ({
      stlpec,
      smer: prev.stlpec === stlpec && prev.smer === "asc" ? "desc" : "asc"
    }));
  };

  const ikonaZoradenia = (stlpec) => {
    if (zoradenie.stlpec !== stlpec) return "↕";
    return zoradenie.smer === "asc" ? "↑" : "↓";
  };

  const nacitajMeta = React.useCallback(async () => {
    const r = await fetch(apiUrl("/api/admin/hypoteky/meta"), {
      credentials: "include"
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.message || "Meta dáta sa nepodarilo načítať.");
    setMeta(data || {});
  }, []);

  const nacitajKampane = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (filterBankaKod) params.set("bankaKod", filterBankaKod);
      if (filterStav) params.set("stav", filterStav);

      const r = await fetch(apiUrl(`/api/admin/hypoteky/kampane?${params.toString()}`), {
        credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Kampane sa nepodarilo načítať.");
      setKampane(Array.isArray(data?.kampane) ? data.kampane : []);
    } catch (e) {
      setError(e.message || "Kampane sa nepodarilo načítať.");
      setKampane([]);
    } finally {
      setLoading(false);
    }
  }, [filterBankaKod, filterStav]);

  React.useEffect(() => {
    nacitajMeta().catch((e) => setError(e.message || "Meta dáta sa nepodarilo načítať."));
  }, [nacitajMeta]);

  React.useEffect(() => {
    nacitajKampane();
  }, [nacitajKampane]);

  const formularJeKompletny = React.useMemo(() => {
    if (!formular.bankaKod || !formular.nazov.trim() || !formular.datumOd) return false;
    if (formular.sadzbaTyp !== "ZIADNY" && !hasCislo(formular.sadzbaHodnota)) return false;
    if (formular.poplatokTyp !== "ZIADNY" && !hasCislo(formular.poplatokHodnota)) return false;

    if (formular.vekRezim === "jednoduchy") {
      return hasCislo(formular.vekMin) || hasCislo(formular.vekMax);
    }

    if (formular.vekRezim === "pravidla") {
      return formular.vekPravidla.some((p) =>
        p.limity.some((l) => hasCislo(l.min) || hasCislo(l.max))
      );
    }

    return true;
  }, [formular]);

  const nastavPole = (nazov, hodnota) => {
    setFormular((prev) => ({
      ...prev,
      [nazov]: hodnota
    }));
    setModalError("");
  };

  const prepniPole = (nazov, hodnota) => {
    setFormular((prev) => {
      const aktualne = new Set(prev[nazov] || []);
      if (aktualne.has(hodnota)) aktualne.delete(hodnota);
      else aktualne.add(hodnota);
      return {
        ...prev,
        [nazov]: Array.from(aktualne)
      };
    });
    setModalError("");
  };

  const nastavVyberCisel = (nazov, hodnota) => {
    const cislo = Number(hodnota);
    if (!Number.isFinite(cislo) || cislo <= 0) return;

    setFormular((prev) => {
      const aktualne = new Set((prev[nazov] || []).map(Number));
      if (aktualne.has(cislo)) aktualne.delete(cislo);
      else aktualne.add(cislo);

      return {
        ...prev,
        vlastnaFixaciaHodnota: "",
        [nazov]: Array.from(aktualne).sort((a, b) => a - b)
      };
    });

    setModalError("");
  };

  const nastavVlastnuFixaciu = (hodnota) => {
    setFormular((prev) => ({
      ...prev,
      vlastnaFixaciaHodnota: hodnota,
      fixacieRoky: hodnota ? [] : prev.fixacieRoky,
      fixacieMesiace: hodnota ? [] : prev.fixacieMesiace
    }));
    setModalError("");
  };

  const nastavTypVlastnejFixacie = (hodnota) => {
    setFormular((prev) => ({
      ...prev,
      vlastnaFixaciaTyp: hodnota
    }));
    setModalError("");
  };

  const nastavManualneVypnutie = (vypnute) => {
    setFormular((prev) => ({
      ...prev,
      aktivna: !vypnute
    }));
    setModalError("");
  };


  const otvorPridanie = () => {
    setEditKampan(null);
    setFormular({
      ...prazdnyFormular,
      bankaKod: filterBankaKod || "",
      datumOd: dnes
    });
    setModalError("");
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const otvorUpravu = (kampan) => {
    setEditKampan(kampan);
    setFormular(formularZKampane(kampan, meta));
    setModalError("");
    setMessage("");
    setError("");
    setModalOpen(true);
  };

  const nastavVekPravidlo = (index, patch) => {
    setFormular((prev) => ({
      ...prev,
      vekPravidla: prev.vekPravidla.map((p, i) => i === index ? { ...p, ...patch } : p)
    }));
    setModalError("");
  };

  const nastavVekLimit = (pravidloIndex, limitIndex, patch) => {
    setFormular((prev) => ({
      ...prev,
      vekPravidla: prev.vekPravidla.map((p, i) => i !== pravidloIndex
        ? p
        : {
            ...p,
            limity: p.limity.map((l, j) => j === limitIndex ? { ...l, ...patch } : l)
          })
    }));
    setModalError("");
  };


  const nastavVekSablonu = (kod) => {
    setFormular((prev) => ({
      ...prev,
      vekPravidla: [vytvorPravidloZoSablony(kod)]
    }));
    setModalError("");
  };

  const pridajVekLimit = (pravidloIndex) => {
    if (formular.vekPravidla[pravidloIndex].limity.length >= MAX_POCET_VEKOVYCH_LIMITOV) return;
    setFormular((prev) => ({
      ...prev,
      vekPravidla: prev.vekPravidla.map((p, i) => i !== pravidloIndex
        ? p
        : {
            ...p,
            limity: [
              ...p.limity,
              {
                faza: "PODANIE",
                koho: "VSETCI",
                min: "",
                max: ""
              }
            ]
          })
    }));
  };

  const ulozKampan = async () => {
    if (!formularJeKompletny || modalSaving) return;
    setModalSaving(true);
    setModalError("");

    try {
      const jeEdit = Boolean(editKampan?.id);
      const r = await fetch(
        apiUrl(jeEdit ? `/api/admin/hypoteky/kampane/${editKampan.id}` : "/api/admin/hypoteky/kampane"),
        {
          method: jeEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(formularNaBody(formular))
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Kampaň sa nepodarilo uložiť.");

      setModalOpen(false);
      setMessage(data?.message || "Kampaň bola uložená.");
      await nacitajKampane();
    } catch (e) {
      setModalError(e.message || "Kampaň sa nepodarilo uložiť.");
    } finally {
      setModalSaving(false);
    }
  };

  const potvrditVymazanie = async () => {
    if (!deleteKampan?.id || deleting) return;
    setDeleting(true);
    setDeleteError("");

    try {
      const r = await fetch(apiUrl(`/api/admin/hypoteky/kampane/${deleteKampan.id}`), {
        method: "DELETE",
        credentials: "include"
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Kampaň sa nepodarilo vymazať.");

      setDeleteKampan(null);
      setMessage(data?.message || "Kampaň bola vymazaná.");
      await nacitajKampane();
    } catch (e) {
      setDeleteError(e.message || "Kampaň sa nepodarilo vymazať.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="card-section admin-users-card">
        <div className="admin-users-header">
          <div>
            <h2>Kampane</h2>
            <div className="form_hint">
              Správa kampaňových podmienok, efektov a platnosti bez ručného zásahu do DB.
            </div>
          </div>

          <div className="admin-users-header-actions">
            <button type="button" className="action-btn" onClick={nacitajKampane} disabled={loading}>
              {loading ? "Načítavam..." : "Obnoviť"}
            </button>
            <button type="button" className="action-btn" onClick={otvorPridanie}>
              + Pridať kampaň
            </button>
          </div>
        </div>

        <div className="admin-users-toolbar admin-hypoteky-toolbar">
          <select
            className="form_select admin-hypoteky-filter"
            value={filterBankaKod}
            onChange={(e) => setFilterBankaKod(e.target.value)}
          >
            <option value="">Všetky banky</option>
            {banky.map((b) => (
              <option key={b.bankaKod} value={b.bankaKod}>
                {b.bankaKod}
              </option>
            ))}
          </select>

          <select
            className="form_select admin-hypoteky-filter"
            value={filterStav}
            onChange={(e) => setFilterStav(e.target.value)}
          >
            {stavOptions.map((o) => (
              <option key={o.kod || "vsetky"} value={o.kod}>
                {o.nazov}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="admin-users-message admin-users-message--error">{error}</div>}
        {message && <div className="admin-users-message">{message}</div>}

        <div className="admin-users-table-wrap">
          <table className="admin-users-table admin-kampane-table">
            <thead>
              <tr>
                <th>
                  <button type="button" className="admin-sort-btn" onClick={() => prepniZoradenie("banka")}>
                    Banka <span className="admin-sort-mark">{ikonaZoradenia("banka")}</span>
                  </button>
                </th>
                <th>Kampaň</th>
                <th>
                  <button type="button" className="admin-sort-btn" onClick={() => prepniZoradenie("platnost")}>
                    Platnosť <span className="admin-sort-mark">{ikonaZoradenia("platnost")}</span>
                  </button>
                </th>
                <th>
                  <button type="button" className="admin-sort-btn" onClick={() => prepniZoradenie("stav")}>
                    Stav <span className="admin-sort-mark">{ikonaZoradenia("stav")}</span>
                  </button>
                </th>
                <th>Efekt</th>
                <th>
                  <button type="button" className="admin-sort-btn" onClick={() => prepniZoradenie("priorita")}>
                    Priorita <span className="admin-sort-mark">{ikonaZoradenia("priorita")}</span>
                  </button>
                </th>
                <th>Akcie</th>
              </tr>
            </thead>
            <tbody>
              {!loading && zobrazeneKampane.map((k) => (
                <tr key={k.id}>
                  <td data-label="Banka">{k.bankaKod}</td>
                  <td data-label="Kampaň">{k.nazov}</td>
                  <td data-label="Platnosť">
                    {formatDatum(k.datumOd)} – {formatDatum(k.datumDo)}
                  </td>
                  <td data-label="Stav">
                    <span className={stavClass(k.stav)}>{nazovStavu(k.stav)}</span>
                  </td>
                  <td data-label="Efekt">{popisEfektu(k)}</td>
                  <td data-label="Priorita">{k.meta?.priorita ?? "-"}</td>
                  <td data-label="Akcie">
                    <div className="admin-users-row-actions">
                      <button type="button" className="action-btn" onClick={() => otvorUpravu(k)}>
                        Upraviť
                      </button>
                      <button
                        type="button"
                        className="action-btn admin-danger-btn"
                        onClick={() => {
                          setDeleteError("");
                          setDeleteKampan(k);
                        }}
                      >
                        Vymazať
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {loading && <div className="admin-users-empty">Načítavam kampane...</div>}
          {!loading && !kampane.length && (
            <div className="admin-users-empty">Nenašli sa žiadne kampane.</div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="pdf-modal-overlay" onClick={() => !modalSaving && setModalOpen(false)}>
          <div className="pdf-modal admin-kampane-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal__header admin-kampane-modal-header">
              <h2 className="pdf-modal__title">
                {editKampan ? "Upraviť kampaň" : "Pridať kampaň"}
              </h2>
              {editKampan && (
                <label className="admin-access-option admin-kampane-header-toggle">
                  <input type="checkbox" checked={!formular.aktivna} onChange={(e) => nastavManualneVypnutie(e.target.checked)} />
                  <span>Manuálne vypnúť kampaň</span>
                </label>
              )}
            </div>

            <div className="pdf-modal__body">
              <div className="admin-kampane-section">
                <h3 className="admin-kampane-heading-with-hint"><span>Identifikácia</span> <InfoIkona obsah={infoTexty.identifikacia} nazov="Identifikácia" /></h3>
                <div className="admin-kampane-ident-grid">
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Banka</label>
                    <select className="pdf-modal__input admin-kampane-compact-input" value={formular.bankaKod} onChange={(e) => nastavPole("bankaKod", e.target.value)}>
                      <option value="">Vyber banku</option>
                      {banky.map((b) => (
                        <option key={b.bankaKod} value={b.bankaKod}>{b.bankaKod}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Platnosť od</label>
                    <input className="pdf-modal__input admin-kampane-compact-input" type="date" value={formular.datumOd} onChange={(e) => nastavPole("datumOd", e.target.value)} />
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Platnosť do</label>
                    <input
                      className="pdf-modal__input admin-kampane-compact-input"
                      type="date"
                      value={formular.datumDo}
                      disabled={formular.datumDoNeurcito}
                      onChange={(e) => nastavPole("datumDo", e.target.value)}
                    />
                  </div>
                  <label className="admin-access-option admin-kampane-toggle admin-kampane-ident-toggle">
                    <input
                      type="checkbox"
                      checked={formular.datumDoNeurcito}
                      onChange={(e) =>
                        setFormular((prev) => ({
                          ...prev,
                          datumDoNeurcito: e.target.checked,
                          datumDo: e.target.checked ? "" : prev.datumDo
                        }))
                      }
                    />
                    <span>Na neurčito</span>
                  </label>
                  <div className="pdf-modal__field admin-kampane-name-field">
                    <label className="pdf-modal__label">Názov kampane (max znakov 80)</label>
                    <input className="pdf-modal__input" maxLength={MAX_DLZKA_NAZVU_KAMPANE} value={formular.nazov} onChange={(e) => nastavPole("nazov", e.target.value)} />
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Priorita</label>
                    <input className="pdf-modal__input admin-kampane-compact-input" inputMode="numeric" value={formular.priorita} onChange={(e) => nastavPole("priorita", e.target.value.replace(/\D/g, ""))} />
                  </div>
                </div>
              </div>

              <div className="admin-kampane-section">
                <h3 className="admin-kampane-heading-with-hint"><span>Efekt</span> <InfoIkona obsah={infoTexty.efekt} nazov="Efekt" /></h3>
                <div className="admin-kampane-effect-row">
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Sadzba</label>
                    <select className="pdf-modal__input admin-kampane-effect-select" value={formular.sadzbaTyp} onChange={(e) => nastavPole("sadzbaTyp", e.target.value)}>
                      <option value="ZIADNY">Bez efektu</option>
                      <option value="ZLAVA">Zľava zo sadzby</option>
                      <option value="PEVNA">Pevná sadzba</option>
                    </select>
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">
                      {formular.sadzbaTyp === "ZLAVA" ? "Zľava (%)" : "Sadzba (%)"}
                    </label>
                    <input
                      className="pdf-modal__input admin-kampane-compact-input"
                      inputMode="decimal"
                      disabled={formular.sadzbaTyp === "ZIADNY"}
                      value={formular.sadzbaTyp === "ZIADNY" ? "" : formular.sadzbaHodnota}
                      onChange={(e) => nastavPole("sadzbaHodnota", e.target.value.replace(/[^0-9,.]/g, ""))}
                    />
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Poplatok</label>
                    <select className="pdf-modal__input admin-kampane-effect-select" value={formular.poplatokTyp} onChange={(e) => nastavPole("poplatokTyp", e.target.value)}>
                      <option value="ZIADNY">Bez efektu</option>
                      <option value="PERCENTO">Zľava z poplatku v %</option>
                      <option value="FIX">Nový poplatok v €</option>
                    </select>
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">
                      {formular.poplatokTyp === "PERCENTO" ? "Zľava (%)" : "Poplatok (€)"}
                    </label>
                    <input
                      className="pdf-modal__input admin-kampane-compact-input"
                      inputMode="decimal"
                      disabled={formular.poplatokTyp === "ZIADNY"}
                      value={formular.poplatokTyp === "ZIADNY" ? "" : formular.poplatokHodnota}
                      onChange={(e) => nastavPole("poplatokHodnota", e.target.value.replace(/[^0-9,.]/g, ""))}
                    />
                  </div>
                </div>
              </div>

              <div className="admin-kampane-section">
                <h3 className="admin-kampane-heading-with-hint"><span>Podmienky</span> <InfoIkona obsah={infoTexty.podmienky} nazov="Podmienky" /></h3>

                <div className="admin-kampane-subsection">
                  <h4 className="admin-kampane-heading-with-hint"><span>Typ hypotéky</span> <InfoIkona obsah={infoTexty.typHypoteky} nazov="Typ hypotéky" /></h4>
                  <div className="admin-kampane-condition-line">
                    <div className="admin-kampane-checkgrid admin-kampane-checkgrid--four">
                      {typyHypoteky.map((typ) => (
                        <label key={typ} className="admin-access-option">
                          <input type="checkbox" checked={formular.typyHypoteky.includes(typ)} onChange={() => prepniPole("typyHypoteky", typ)} />
                          <span>{typ}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="admin-kampane-subsection">
                  <h4 className="admin-kampane-heading-with-hint"><span>Fixácia</span> <InfoIkona obsah={infoTexty.fixacia} nazov="Fixácia" /></h4>
                  <div className="admin-kampane-fix-row">
                  <div className="pdf-modal__field admin-kampane-fix-existing">
                    <label className="pdf-modal__label">Výber fixácií</label>
                    <details className={`admin-kampane-multiselect ${formular.vlastnaFixaciaHodnota ? "is-disabled" : ""}`}>
                      <summary onClick={(e) => formular.vlastnaFixaciaHodnota && e.preventDefault()}>{popisFixacii(formular)}</summary>
                      <div className="admin-kampane-multiselect-panel">
                        {!!fixacieRokyOptions.length && (
                          <div className="admin-kampane-multiselect-group">
                            <div className="admin-kampane-mini-heading">Ročné</div>
                            {fixacieRokyOptions.map((rok) => (
                              <label key={`rok-${rok}`} className="admin-access-option">
                                <input type="checkbox" disabled={!!formular.vlastnaFixaciaHodnota} checked={formular.fixacieRoky.includes(rok)} onChange={() => nastavVyberCisel("fixacieRoky", rok)} />
                                <span>{rok}R</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {!!fixacieMesiaceOptions.length && (
                          <div className="admin-kampane-multiselect-group">
                            <div className="admin-kampane-mini-heading">Mesačné</div>
                            {fixacieMesiaceOptions.map((mesiace) => (
                              <label key={`mesiac-${mesiace}`} className="admin-access-option">
                                <input type="checkbox" disabled={!!formular.vlastnaFixaciaHodnota} checked={formular.fixacieMesiace.includes(mesiace)} onChange={() => nastavVyberCisel("fixacieMesiace", mesiace)} />
                                <span>{mesiace}M</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {!fixacieRokyOptions.length && !fixacieMesiaceOptions.length && (
                          <div className="form_hint">Pre túto banku nie sú v sadzbách evidované fixácie. Zadaj vlastnú.</div>
                        )}
                      </div>
                    </details>
                  </div>
                  <span className="admin-kampane-or">alebo</span>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Vlastná fixácia</label>
                    <input
                      className="pdf-modal__input admin-kampane-compact-input"
                      inputMode="numeric"
                      disabled={formular.fixacieRoky.length > 0 || formular.fixacieMesiace.length > 0}
                      value={formular.vlastnaFixaciaHodnota}
                      onChange={(e) => nastavVlastnuFixaciu(e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Typ fixácie</label>
                    <select
                      className="pdf-modal__input admin-kampane-compact-input"
                      disabled={formular.fixacieRoky.length > 0 || formular.fixacieMesiace.length > 0}
                      value={formular.vlastnaFixaciaTyp}
                      onChange={(e) => nastavTypVlastnejFixacie(e.target.value)}
                    >
                      <option value="rocna">Ročná</option>
                      <option value="mesacna">Mesačná</option>
                    </select>
                  </div>
                  </div>
                </div>

                <div className="admin-kampane-subsection">
                  <h4 className="admin-kampane-heading-with-hint"><span>LTV</span> <InfoIkona obsah={infoTexty.ltv} nazov="LTV" /></h4>
                  <div className="admin-kampane-ltv-line">
                    <div className="pdf-modal__field">
                      <label className="pdf-modal__label">LTV max (%)</label>
                      <input className="pdf-modal__input admin-kampane-compact-input" inputMode="decimal" value={formular.ltvMax} onChange={(e) => nastavPole("ltvMax", e.target.value.replace(/[^0-9,.]/g, ""))} />
                    </div>
                  </div>
                </div>

                <div className="admin-kampane-subsection">
                  <h4 className="admin-kampane-heading-with-hint">
                    <span>Vek</span>
                    <InfoIkona obsah={infoTexty.vek} nazov="Vek" />
                  </h4>
                  <div className="admin-kampane-age-row">
                    <div className="pdf-modal__field">
                      <label className="pdf-modal__label">Režim</label>
                      <select className="pdf-modal__input admin-kampane-small-select" value={formular.vekRezim} onChange={(e) => nastavPole("vekRezim", e.target.value)}>
                        {vekRezimOptions.map((o) => <option key={o.kod} value={o.kod}>{o.nazov}</option>)}
                      </select>
                    </div>
                    {formular.vekRezim === "jednoduchy" && (
                      <>
                        <div className="pdf-modal__field">
                          <label className="pdf-modal__label">Platí pre</label>
                          <select className="pdf-modal__input admin-kampane-small-select" value={formular.vekAplikacia} onChange={(e) => nastavPole("vekAplikacia", e.target.value)}>
                            {(meta?.kampane?.vek?.jednoducheAplikacie || []).map((v) => <option key={v} value={v}>{labelJednoduchaAplikacia(v)}</option>)}
                          </select>
                        </div>
                        <div className="pdf-modal__field">
                          <label className="pdf-modal__label">Min vek (r)</label>
                          <input className="pdf-modal__input admin-kampane-compact-input" inputMode="numeric" value={formular.vekMin} onChange={(e) => nastavPole("vekMin", e.target.value.replace(/\D/g, ""))} />
                        </div>
                        <div className="pdf-modal__field">
                          <label className="pdf-modal__label">Max vek (r)</label>
                          <input className="pdf-modal__input admin-kampane-compact-input" inputMode="numeric" value={formular.vekMax} onChange={(e) => nastavPole("vekMax", e.target.value.replace(/\D/g, ""))} />
                        </div>
                      </>
                    )}
                  </div>

                  {formular.vekRezim === "pravidla" && (
                    <div className="admin-kampane-rules">
                      <div className="admin-kampane-chipgrid admin-kampane-chipgrid--compact">
                        {vekPravidloSablony.map((sablona) => (
                          <button
                            type="button"
                            key={sablona.kod}
                            className="admin-kampane-chip"
                            onClick={() => nastavVekSablonu(sablona.kod)}
                          >
                            {sablona.nazov}
                          </button>
                        ))}
                      </div>

                      {formular.vekPravidla.map((pravidlo, i) => (
                        <div className="admin-kampane-rule" key={`pravidlo-${i}`}>
                          <div className="admin-kampane-rule-summary">
                            {vekPravidloSablony.find((s) => s.preKoho === pravidlo.preKoho && s.aplikacia === pravidlo.aplikacia)?.nazov || "Vlastné vekové pravidlo"}
                          </div>
                          <div className="admin-kampane-age-rule-row">
                            <div className="pdf-modal__field"><div className="pdf-modal__label">Pre koho <InfoIkona obsah={infoTexty.preKoho} nazov="Pre koho" /></div>
<select aria-label="Pre koho" className="pdf-modal__input admin-kampane-small-select" value={pravidlo.preKoho} onChange={(e) => nastavVekPravidlo(i, { preKoho: e.target.value })}>
                              {(meta?.kampane?.vek?.preKoho || []).map((v) => <option key={v} value={v}>{labelPreKoho(v)}</option>)}
                            </select>
</div>
                            <div className="pdf-modal__field"><div className="pdf-modal__label">Aplikácia <InfoIkona obsah={infoTexty.aplikacia} nazov="Aplikácia" /></div>
<select aria-label="Aplikácia" className="pdf-modal__input admin-kampane-rule-select" value={pravidlo.aplikacia} onChange={(e) => nastavVekPravidlo(i, { aplikacia: e.target.value })}>
                              {(meta?.kampane?.vek?.aplikaciePravidla || []).map((v) => <option key={v} value={v}>{labelAplikaciaPravidla(v)}</option>)}
                            </select>
</div>
                          </div>

                          {pravidlo.limity.map((limit, j) => (
                            <div className="admin-kampane-limit" key={`limit-${i}-${j}`}>
                              <div className="pdf-modal__field"><div className="pdf-modal__label">Fáza <InfoIkona obsah={infoTexty.faza} nazov="Fáza" /></div>
<select aria-label="Fáza" className="pdf-modal__input admin-kampane-small-select" value={limit.faza} onChange={(e) => nastavVekLimit(i, j, { faza: e.target.value })}>
                                {(meta?.kampane?.vek?.fazy || []).map((v) => <option key={v} value={v}>{labelFaza(v)}</option>)}
                              </select>
</div>
                              <div className="pdf-modal__field"><div className="pdf-modal__label">Kto <InfoIkona obsah={infoTexty.koho} nazov="Koho" /></div>
<select aria-label="Koho" className="pdf-modal__input admin-kampane-small-select" value={limit.koho} onChange={(e) => nastavVekLimit(i, j, { koho: e.target.value })}>
                                {(meta?.kampane?.vek?.koho || []).map((v) => <option key={v} value={v}>{labelKoho(v)}</option>)}
                              </select>
</div>
                              <label className="pdf-modal__field"><span className="pdf-modal__label">Min (r)</span><input className="pdf-modal__input admin-kampane-compact-input" placeholder="Min r" inputMode="numeric" value={limit.min} onChange={(e) => nastavVekLimit(i, j, { min: e.target.value.replace(/\D/g, "") })} /></label>
                              <label className="pdf-modal__field"><span className="pdf-modal__label">Max (r)</span><input className="pdf-modal__input admin-kampane-compact-input" placeholder="Max r" inputMode="numeric" value={limit.max} onChange={(e) => nastavVekLimit(i, j, { max: e.target.value.replace(/\D/g, "") })} /></label>
                              <button type="button" className="action-btn admin-danger-btn" aria-label="Odobrať limit" onClick={() => nastavVekPravidlo(i, { limity: pravidlo.limity.filter((_, idx) => idx !== j) })}>−</button>
                            </div>
                          ))}

                          <button type="button" className="action-btn admin-kampane-inline-btn" disabled={pravidlo.limity.length >= MAX_POCET_VEKOVYCH_LIMITOV} onClick={() => pridajVekLimit(i)}>
                            + Limit
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="admin-kampane-subsection">
                  <h4 className="admin-kampane-heading-with-hint"><span>Checkboxy (účet, poistenie nehnuteľnosti, poistenie úveru, EKO certifikát)</span> <InfoIkona obsah={infoTexty.checkboxy} nazov="Checkboxy" /></h4>
                  <div className="admin-kampane-checkbox-condition">
                    <div className="pdf-modal__field">
                      <label className="pdf-modal__label">Checkboxy musia byť zapnuté</label>
                    <select
                      className="pdf-modal__input admin-kampane-condition-select"
                      value={formular.musiaBytZapnuteAktivne ? "ANO" : "NIE"}
                      onChange={(e) =>
                        setFormular((prev) => ({
                          ...prev,
                          musiaBytZapnuteAktivne: e.target.value === "ANO",
                          musiaBytZapnute: e.target.value === "ANO" ? prev.musiaBytZapnute : []
                        }))
                      }
                    >
                      <option value="NIE">Bez checkbox podmienky</option>
                      <option value="ANO">Vyžadovať zapnuté checkboxy</option>
                    </select>
                  </div>
                    {formular.musiaBytZapnuteAktivne && (
                      <div className="admin-kampane-checkgrid admin-kampane-checkgrid--four admin-kampane-checkbox-line">
                        {checkboxy.map((c) => (
                          <label key={`musia-${c.kod}`} className="admin-access-option">
                            <input type="checkbox" checked={formular.musiaBytZapnute.includes(c.kod)} onChange={() => prepniPole("musiaBytZapnute", c.kod)} />
                            <span>{c.nazov}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="admin-kampane-section">
                <h3 className="admin-kampane-heading-with-hint">
                  <span>Správanie checkboxových efektov</span>
                  <InfoIkona obsah={infoTexty.spravanieCheckboxov} nazov="Správanie checkboxových efektov" />
                </h3>
                <div className="admin-kampane-checkbox-behaviour">
                  <div className="pdf-modal__field">
                    <label className="pdf-modal__label">Režim</label>
                    <select
                      className="pdf-modal__input admin-kampane-condition-select"
                      value={formular.spravanieRezim}
                      onChange={(e) =>
                        setFormular((prev) => ({
                          ...prev,
                          spravanieRezim: e.target.value,
                          vypnuteCheckboxy: e.target.value === "VYPNUT_EFEKT" ? prev.vypnuteCheckboxy : []
                        }))
                      }
                    >
                      <option value="STANDARD">Štandardne - nič nevypínať</option>
                      <option value="VYPNUT_EFEKT">Vypnúť efekt vybraných checkboxov</option>
                    </select>
                  </div>

                  {formular.spravanieRezim === "VYPNUT_EFEKT" && (
                    <div className="admin-kampane-checkgrid admin-kampane-checkgrid--four admin-kampane-checkbox-line">
                      {checkboxy.map((c) => (
                        <label key={`vypnute-${c.kod}`} className="admin-access-option">
                          <input type="checkbox" checked={formular.vypnuteCheckboxy.includes(c.kod)} onChange={() => prepniPole("vypnuteCheckboxy", c.kod)} />
                          <span>{c.nazov}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-kampane-section">
                <h3 className="admin-kampane-heading-with-hint"><span>Poznámka</span> <InfoIkona obsah={infoTexty.poznamka} nazov="Poznámka" /></h3>
                <div className="pdf-modal__field">
                  <label className="pdf-modal__label">Text kampane (max znakov 600)</label>
                  <textarea className="pdf-modal__input admin-kampane-textarea" maxLength={MAX_DLZKA_POZNAMKY_KAMPANE} value={formular.hintText} onChange={(e) => nastavPole("hintText", e.target.value)} />
                </div>
              </div>

              <div className="admin-kampane-section">
                <h3>Príloha</h3>
                <div className="form_hint admin-hypoteky-help">
                  Príloha je pripravená v dátovej štruktúre. Samotné nahrávanie PDF napojíme v ďalšom kroku.
                </div>
              </div>

              {modalError && <div className="admin-modal-error">{modalError}</div>}
            </div>

            <div className="pdf-modal__footer">
              {editKampan && (
                <button
                  type="button"
                  className="action-btn admin-danger-btn"
                  disabled={modalSaving}
                  onClick={() => {
                    setDeleteError("");
                    setDeleteKampan(editKampan);
                    setModalOpen(false);
                  }}
                >
                  Vymazať
                </button>
              )}
              <button type="button" className="action-btn" disabled={modalSaving} onClick={() => setModalOpen(false)}>
                Zrušiť
              </button>
              <button type="button" className="action-btn" disabled={!formularJeKompletny || modalSaving} onClick={ulozKampan}>
                {modalSaving ? "Ukladám..." : "Uložiť"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteKampan && (
        <div className="pdf-modal-overlay" onClick={() => !deleting && setDeleteKampan(null)}>
          <div className="pdf-modal admin-delete-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal__header">
              <h2 className="pdf-modal__title">Vymazať kampaň</h2>
            </div>
            <div className="pdf-modal__body">
              <p className="admin-delete-modal__text">
                Naozaj chcete vymazať kampaň {deleteKampan.bankaKod ? `${deleteKampan.bankaKod} - ` : ""}<strong>{deleteKampan.nazov}</strong>?
              </p>
              {deleteError && <div className="admin-modal-error">{deleteError}</div>}
            </div>
            <div className="pdf-modal__footer">
              <button type="button" className="action-btn" disabled={deleting} onClick={() => setDeleteKampan(null)}>
                Zrušiť
              </button>
              <button type="button" className="action-btn admin-danger-btn" disabled={deleting} onClick={potvrditVymazanie}>
                {deleting ? "Mažem..." : "Vymazať"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminHypotekyKampane;
